import { FormEvent, useState } from "react"
import { Link } from "react-router-dom"
import ZodForm, { useZodFormIssues } from "react-zod-form"
import { z } from "zod"

import useTheme from "@/services/Theme/useTheme"
import Button from "@/shared/components/intrinsic/Button/Button"
import Icon from "@/shared/components/intrinsic/Icon/Icon"
import useFormStatus from "@/utils/hooks/useFormStatus"

import { passwordPattern } from "../../helpers"
import NewPasswordFields from "../PasswordField/NewPasswordFields"

interface NewPasswordFormProps {
  onSubmit?(password: string): boolean | Promise<boolean>
}

const form = new ZodForm({
  password: z.coerce.string()
    .regex(
      passwordPattern,
      "Password must contain at least 8 characters, including: upper case, lower case, number and a special character"
    ),
  confirmPassword: z.coerce.string()

})

function NewPasswordForm(props: NewPasswordFormProps) {

  const [operationSuccessful, setOperationSuccessful] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { fieldIssues, addIssues } = useZodFormIssues(form)
  const theme = useTheme()
  const formStatus = useFormStatus(form, onSubmit)
  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    const fields = form.parseAllFields(event)
    if (!fields) return
    const { password, confirmPassword } = fields

    if (password !== confirmPassword) {
      addIssues(["confirmPassword"], ["Passwords do not match"])
      return
    }

    setIsSubmitting(true)
    try {
      const success = await props.onSubmit?.(password)
      setOperationSuccessful(success ?? false)
    } catch (error) {
      console.error("Failed to set password:", error)
      setOperationSuccessful(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onChange={form.parseAllFields.bind(form)} onSubmit={formStatus.submit}>
      <NewPasswordFields
        theme={theme}
        password={form.fields.password}
        confirmPassword={form.fields.confirmPassword}
        passwordCustomValidity={fieldIssues.password}
        confirmPasswordCustomValidity={fieldIssues.confirmPassword}
      />
      <Button
        size="big"
        type="submit"
        color={operationSuccessful ? "green" : undefined}
        disabled={operationSuccessful || isSubmitting}
      >
        {operationSuccessful ? (
          <>
            Password set successful <Icon name="check" />
          </>
        ) : isSubmitting ? (
          "Setting password..."
        ) : (
          "Set password"
        )}
      </Button>

      {operationSuccessful && (
        <Link
          to="/user/login"
          style={{
            position: "absolute",
            top: "200px",
            right: "101px",
            color: "#404043",
            textDecoration: "underline"
          }}
        >
          Redirecting to the login page in 5 sec...
        </Link>
      )}
    </form>
  )
}

export default NewPasswordForm
